/* ATB menu redesign (2026-10): behaviour for the computer mega menu (<atb-mega>) and the phone menu
   (<atb-menu-drawer>). Markup: snippets/header-mega-menu.liquid and snippets/header-drawer.liquid.
   Computer: W3C disclosure navigation. Each category has a button with aria-expanded; a panel opens after the
   mouse rests 0.3 s (switching between open panels is quicker), closes 0.3 s after the mouse leaves, opens on
   click, and Esc closes it and returns focus to its button. Touch screens: the first tap on a category opens its
   panel ("Shop all" is the first link inside).
   Phone: a full-screen dialog. One step into a category; the in-menu back button and the phone's own back
   gesture both step back; the menu always reopens on its first screen. */
(function () {
  'use strict';
  if (window.__atbMenu) return;
  window.__atbMenu = true;

  var OPEN_DELAY = 300;
  var SWITCH_DELAY = 150;
  var CLOSE_DELAY = 300;
  var desktopMQ = window.matchMedia('(min-width: 990px)');
  var hoverMQ = window.matchMedia('(hover: hover) and (pointer: fine)'); // fallback when no pointer event was seen

  /* ------------------------------------------------------------------ computer */
  class AtbMega extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.current = null;
      this.bar = this.querySelector('.atb-bar');
      this.scrim = this.querySelector('.atb-scrim');
      this.items = Array.prototype.slice.call(this.querySelectorAll('.atb-bar__item[data-panel]'));
      var self = this;

      // A page that loads under a resting cursor gets pointerenter with no movement, and a panel popped open on every
      // page change. Hover opens panels only after the mouse has really moved (the first move counts as a hover).
      this.moved = false;
      var firstMove = function (e) {
        if (e.pointerType !== 'mouse') return;
        self.moved = true;
        window.removeEventListener('pointermove', firstMove);
        var li = e.target && e.target.closest ? e.target.closest('.atb-bar__item[data-panel]') : null;
        if (li && self.items.indexOf(li) !== -1 && self.current !== li) {
          clearTimeout(self.tOpen);
          self.tOpen = setTimeout(function () { self.open(li); }, OPEN_DELAY);
        }
      };
      window.addEventListener('pointermove', firstMove, { passive: true });

      this.items.forEach(function (li) {
        var btn = li.querySelector('.atb-bar__toggle');
        var link = li.querySelector('a.atb-bar__link');
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          clearTimeout(self.tOpen);
          // Hover opens a panel after a short delay, so a click that follows it found the panel open and closed it
          // again: the menu flashed. A click soon after hover opened it keeps it open (owner, 2026-10-26).
          if (self.current === li && self.openedBy === 'hover' && Date.now() - self.openedTime < 2000) {
            self.openedBy = 'click';
            return;
          }
          if (self.current === li) self.close(); else self.open(li, 'click');
        });
        if (link) {
          // Touch and pen: the first tap on a category opens its panel ("Shop all" is the first link inside).
          link.addEventListener('pointerdown', function (e) { self.lastPointer = e.pointerType; });
          link.addEventListener('click', function (e) {
            var touchy = self.lastPointer ? self.lastPointer !== 'mouse' : !hoverMQ.matches;
            if (touchy && self.current !== li) { e.preventDefault(); self.open(li, 'click'); }
          });
        }
        li.addEventListener('pointerenter', function (e) {
          if (e.pointerType !== 'mouse' || !self.moved) return;
          clearTimeout(self.tClose); clearTimeout(self.tOpen);
          if (self.current === li) return;
          self.tOpen = setTimeout(function () { self.open(li); }, self.current ? SWITCH_DELAY : OPEN_DELAY);
        });
        li.addEventListener('pointerleave', function (e) {
          if (e.pointerType !== 'mouse') return;
          clearTimeout(self.tOpen);
          if (self.current) self.tClose = setTimeout(function () { self.close(); }, CLOSE_DELAY);
        });
      });

      this.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' || !self.current) return;
        var btn = self.current.querySelector('.atb-bar__toggle');
        self.close();
        if (btn) btn.focus();
      });
      this.addEventListener('focusout', function (e) {
        if (self.current && e.relatedTarget && !self.contains(e.relatedTarget)) self.close();
      });
      if (this.scrim) this.scrim.addEventListener('click', function () { self.close(); });
      document.addEventListener('click', function (e) { if (self.current && !self.contains(e.target)) self.close(); });
      window.addEventListener('scroll', function () { if (self.current && Math.abs(window.scrollY - self.openedAt) > 80) self.close(); }, { passive: true });
      desktopMQ.addEventListener('change', function () { self.close(); });
    }

    open(li, how) {
      clearTimeout(this.tOpen); clearTimeout(this.tClose);
      if (this.current === li) return;
      if (this.current) this.hide(this.current);
      this.current = li;
      this.openedBy = how || 'hover';
      this.openedTime = Date.now();
      this.openedAt = window.scrollY;
      li.classList.add('is-open');
      li.querySelector('.atb-bar__toggle').setAttribute('aria-expanded', 'true');
      var panel = document.getElementById(li.getAttribute('data-panel'));
      if (panel) panel.hidden = false;
      if (this.scrim) this.scrim.hidden = false;
    }

    hide(li) {
      li.classList.remove('is-open');
      li.querySelector('.atb-bar__toggle').setAttribute('aria-expanded', 'false');
      var panel = document.getElementById(li.getAttribute('data-panel'));
      if (panel) panel.hidden = true;
    }

    close() {
      clearTimeout(this.tOpen); clearTimeout(this.tClose);
      if (this.current) this.hide(this.current);
      this.current = null;
      if (this.scrim) this.scrim.hidden = true;
    }
  }
  if (!customElements.get('atb-mega')) customElements.define('atb-mega', AtbMega);

  /* ------------------------------------------------------------------ phone */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])';

  class AtbMenuDrawer extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.btn = this.querySelector('.atb-md-open');
      this.dlg = this.querySelector('.atb-md');
      if (!this.btn || !this.dlg) return;
      // Move the sheet to <body>: the sticky header must not clip or offset a full-screen dialog.
      document.body.appendChild(this.dlg);
      this.isOpen = false;
      this.depth = 0;
      this.view = 'root';
      this.useHistory = !(window.Shopify && window.Shopify.designMode);
      var self = this;

      this.btn.addEventListener('click', function () { self.open(); });
      this.dlg.addEventListener('click', function (e) {
        var t = e.target;
        var opener = t.closest('[data-open]');
        if (opener) { self.show(opener.getAttribute('data-open')); return; }
        if (t.closest('[data-back]')) { self.back(); return; }
        if (t.closest('[data-close]')) { self.close(); return; }
        var a = t.closest('a[href]');
        if (a && !e.defaultPrevented && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && a.target !== '_blank') {
          e.preventDefault();
          self.leaveTo(a.href);
        }
      });
      this.dlg.addEventListener('submit', function (e) {
        var form = e.target;
        if (!form || (form.method || 'get').toLowerCase() !== 'get') return;
        e.preventDefault();
        var url = new URL(form.action, location.href);
        new FormData(form).forEach(function (v, k) { url.searchParams.append(k, v); });
        self.leaveTo(url.toString());
      });
      this.dlg.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { e.preventDefault(); if (self.view !== 'root') self.back(); else self.close(); return; }
        if (e.key === 'Tab') self.trap(e);
      });
      window.addEventListener('popstate', function () { self.onPop(); });
      window.addEventListener('pageshow', function (e) { if (e.persisted && !self.dlg.hidden) self.reset(); });
      desktopMQ.addEventListener('change', function (e) { if (e.matches && self.isOpen) self.close(); });
    }

    // Theme editor re-renders the header: take the moved sheet away with the old element.
    disconnectedCallback() {
      if (this.dlg && this.dlg.parentNode === document.body && !this.isConnected) {
        document.documentElement.classList.remove('atb-menu-open');
        this.dlg.remove();
      }
    }

    views() { return Array.prototype.slice.call(this.dlg.querySelectorAll('.atb-md__view')); }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.depth = 0;
      this.resetViews();
      this.dlg.hidden = false;
      void this.dlg.offsetWidth;
      this.dlg.classList.add('is-open');
      document.documentElement.classList.add('atb-menu-open');
      this.btn.setAttribute('aria-expanded', 'true');
      this.hydrateFavorites();
      this.push();
      var close = this.dlg.querySelector('.atb-md__view.is-current [data-close]');
      if (close) close.focus({ preventScroll: true });
    }

    push() {
      if (!this.useHistory) return;
      try { history.pushState({ atbMenu: this.depth + 1 }, '', location.href); this.depth++; } catch (err) { /* ignore */ }
    }

    show(name) {
      var next = this.dlg.querySelector('.atb-md__view[data-view="' + name + '"]');
      var cur = this.dlg.querySelector('.atb-md__view.is-current');
      if (!next || next === cur) return;
      next.hidden = false;
      next.classList.add('is-right');
      var sc = next.querySelector('.atb-md__scroll');
      if (sc) sc.scrollTop = 0;
      void next.offsetWidth;
      next.classList.remove('is-right');
      next.classList.add('is-current');
      if (cur) {
        cur.classList.remove('is-current');
        cur.classList.add('is-left');
        setTimeout(function () { if (!cur.classList.contains('is-current')) cur.hidden = true; }, 360);
      }
      this.view = name;
      this.push();
      var h = next.querySelector('.atb-md__h') || next.querySelector('[data-back]');
      if (h) h.focus({ preventScroll: true });
    }

    showRoot() {
      var root = this.dlg.querySelector('.atb-md__view[data-view="root"]');
      var cur = this.dlg.querySelector('.atb-md__view.is-current');
      if (!root || root === cur) return;
      root.hidden = false;
      root.classList.remove('is-left');
      root.classList.add('is-current');
      if (cur) {
        cur.classList.remove('is-current');
        cur.classList.add('is-right');
        setTimeout(function () { if (!cur.classList.contains('is-current')) { cur.hidden = true; cur.classList.remove('is-right'); } }, 360);
      }
      var opener = root.querySelector('[data-open="' + this.view + '"]');
      this.view = 'root';
      if (opener) opener.focus({ preventScroll: true });
    }

    back() {
      if (this.view === 'root') return;
      if (this.useHistory && this.depth > 1) { history.back(); return; }
      this.showRoot();
    }

    onPop() {
      if (!this.isOpen) return;
      this.depth = Math.max(0, this.depth - 1);
      if (this.view !== 'root') this.showRoot(); else this.close(true);
    }

    close(fromPop) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.dlg.classList.remove('is-open');
      document.documentElement.classList.remove('atb-menu-open');
      this.btn.setAttribute('aria-expanded', 'false');
      var dlg = this.dlg;
      setTimeout(function () { if (!dlg.classList.contains('is-open')) dlg.hidden = true; }, 260);
      if (!fromPop) this.unwindHistory();
      this.depth = 0;
      this.btn.focus({ preventScroll: true });
    }

    unwindHistory() {
      if (this.useHistory && this.depth > 0) { var d = this.depth; this.depth = 0; try { history.go(-d); } catch (err) { /* ignore */ } }
    }

    // Leave the page from inside the menu without leaving our history entries behind.
    leaveTo(href) {
      var d = this.useHistory ? this.depth : 0;
      this.isOpen = false;
      this.depth = 0;
      var done = false;
      var go = function () { if (done) return; done = true; window.location.href = href; };
      if (d > 0) {
        window.addEventListener('popstate', go, { once: true });
        try { history.go(-d); } catch (err) { go(); }
        setTimeout(go, 400);
      } else {
        go();
      }
    }

    reset() {
      this.isOpen = false;
      this.depth = 0;
      this.dlg.classList.remove('is-open');
      this.dlg.hidden = true;
      document.documentElement.classList.remove('atb-menu-open');
      this.btn.setAttribute('aria-expanded', 'false');
      this.resetViews();
    }

    resetViews() {
      this.views().forEach(function (v) {
        var root = v.getAttribute('data-view') === 'root';
        v.classList.remove('is-left', 'is-right');
        v.classList.toggle('is-current', root);
        v.hidden = !root;
        var sc = v.querySelector('.atb-md__scroll');
        if (sc) sc.scrollTop = 0;
      });
      this.view = 'root';
    }

    trap(e) {
      var scope = Array.prototype.slice.call(this.dlg.querySelectorAll('.atb-md__view.is-current ' + FOCUSABLE + ', .atb-md__foot ' + FOCUSABLE))
        .filter(function (el) { return el.offsetParent !== null; });
      if (!scope.length) return;
      var first = scope[0], last = scope[scope.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      else if (!this.dlg.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    }

    hydrateFavorites() {
      var badge = this.dlg.querySelector('[data-atb-md-favcount]');
      if (!badge) return;
      try {
        var list = JSON.parse(localStorage.getItem('atb_favorites_local') || '[]');
        if (Array.isArray(list) && list.length) { badge.textContent = list.length > 99 ? '99+' : String(list.length); badge.hidden = false; }
        else badge.hidden = true;
      } catch (err) { badge.hidden = true; }
    }
  }
  if (!customElements.get('atb-menu-drawer')) customElements.define('atb-menu-drawer', AtbMenuDrawer);
})();
